/** Activity scheduling form controls; Rust validates and applies all recurrence and travel rules.
 * @module apps/web/src/components/activities/ActivityScheduleFields
 */
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUserStore } from '@/stores/userStore';

/** Sunday-first labels matching the persisted weekday contract. */
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** Editable weekly rule; authoritative validation belongs to the Rust API. */
interface RoutineDraft {
  /** Target occurrences per local week. */
  occurrences_per_week: number;
  /** IANA timezone defining the week and local fixed start. */
  timezone: string;
  /** Permitted Sunday-zero weekdays; empty means every day. */
  allowed_weekdays: number[];
  /** Whether occurrences require different local dates. */
  distinct_days: boolean;
  /** Optional wall-clock minute for each occurrence. */
  fixed_start_minute?: number | null;
}

/** Render retained custom attributes as routine, fixed-start and travel controls. */
export function ActivityScheduleFields({
  attributes,
  onChange,
}: {
  /** Existing activity metadata, retained through form edits. */
  attributes: Record<string, unknown>;
  /** Update one retained attribute without replacing unrelated fields. */
  onChange: (key: string, value: unknown) => void;
}) {
  const timezone = useUserStore(state => state.timezone);
  const routine = attributes.routine as RoutineDraft | null | undefined;
  const occurrence = Boolean(attributes.routine_id);
  /** Change a single routine form field while retaining the rest of the rule. */
  const updateRoutine = (patch: Partial<RoutineDraft>) =>
    onChange('routine', { ...routine, ...patch });
  return (
    <fieldset className="grid gap-4 rounded-md border p-3">
      <legend className="px-1 text-sm font-medium">Schedule and location</legend>
      {occurrence ? (
        <p className="text-muted-foreground text-sm">
          This is one occurrence of a routine. Its completion is recorded separately.
        </p>
      ) : (
        <div className="flex items-center gap-2">
          <Checkbox
            id="routine-enabled"
            checked={Boolean(routine)}
            onCheckedChange={checked => {
              if (checked) onChange('fixed_start', false);
              onChange(
                'routine',
                checked
                  ? {
                      occurrences_per_week: 3,
                      timezone,
                      allowed_weekdays: [],
                      distinct_days: true,
                    }
                  : null
              );
            }}
          />
          <Label htmlFor="routine-enabled">Repeat as a weekly routine</Label>
        </div>
      )}
      {routine && !occurrence && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="routine-count">Times per week</Label>
              <Input
                id="routine-count"
                type="number"
                min={1}
                max={21}
                step={1}
                value={routine.occurrences_per_week}
                onChange={event =>
                  updateRoutine({ occurrences_per_week: Number(event.target.value) })
                }
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="routine-timezone">Routine timezone</Label>
              <Input
                id="routine-timezone"
                value={routine.timezone}
                onChange={event => updateRoutine({ timezone: event.target.value })}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="routine-distinct"
              checked={routine.distinct_days}
              onCheckedChange={checked => updateRoutine({ distinct_days: checked === true })}
            />
            <Label htmlFor="routine-distinct">Each occurrence on a different day</Label>
          </div>
          <fieldset className="grid gap-2">
            <legend className="mb-2 text-sm font-medium">Allowed days</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DAYS.map((day, index) => (
                <label key={day} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    aria-label={day}
                    checked={routine.allowed_weekdays.includes(index)}
                    onCheckedChange={checked =>
                      updateRoutine({
                        allowed_weekdays: checked
                          ? [...routine.allowed_weekdays, index]
                          : routine.allowed_weekdays.filter(value => value !== index),
                      })
                    }
                  />
                  {day.slice(0, 3)}
                </label>
              ))}
            </div>
            <p className="text-muted-foreground text-xs">
              Leave all days unchecked to allow any day.
            </p>
          </fieldset>
          <div className="grid gap-2">
            <Label htmlFor="routine-time">Fixed start time (optional)</Label>
            <Input
              id="routine-time"
              type="time"
              value={
                routine.fixed_start_minute == null
                  ? ''
                  : `${String(Math.floor(routine.fixed_start_minute / 60)).padStart(2, '0')}:${String(routine.fixed_start_minute % 60).padStart(2, '0')}`
              }
              onChange={event => {
                const [hours, minutes] = event.target.value.split(':').map(Number);
                updateRoutine({
                  fixed_start_minute: event.target.value ? hours! * 60 + minutes! : null,
                });
              }}
            />
            <p className="text-muted-foreground text-xs">
              Each occurrence starts at this local time. Leave blank to let the planner choose.
            </p>
          </div>
        </>
      )}
      {occurrence && attributes.routine_fixed_start === true && (
        <p className="text-muted-foreground text-sm">This occurrence uses the routine's fixed start time.</p>
      )}
      {!routine && attributes.routine_fixed_start !== true && (
        <div className="flex items-center gap-2">
          <Checkbox
            id="activity-fixed-start"
            checked={attributes.fixed_start === true}
            onCheckedChange={checked => onChange('fixed_start', checked === true)}
          />
          <Label htmlFor="activity-fixed-start">Start at a fixed date and time</Label>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="grid gap-2">
          <Label htmlFor="activity-location">Location</Label>
          <Input
            id="activity-location"
            maxLength={200}
            placeholder="e.g. Home, office, gym"
            value={String(attributes.location ?? '')}
            onChange={event => onChange('location', event.target.value || null)}
          />
          {!attributes.location && typeof attributes.predicted_location === 'string' && (
            <p className="text-muted-foreground text-xs">Learned location: {attributes.predicted_location}</p>
          )}
        </div>
        <div className="grid gap-2">
          <Label htmlFor="activity-travel">Estimated travel (minutes)</Label>
          <Input
            id="activity-travel"
            type="number"
            min={0}
            max={720}
            value={typeof attributes.travel_minutes === 'number' ? attributes.travel_minutes : ''}
            onChange={event =>
              onChange(
                'travel_minutes',
                event.target.value === '' ? null : Number(event.target.value)
              )
            }
          />
        </div>
      </div>
      <div className="grid gap-2">
        <Label htmlFor="activity-travel-from">Travel from (optional)</Label>
        <Input
          id="activity-travel-from"
          maxLength={200}
          placeholder="e.g. Home"
          value={String(attributes.travel_from_location ?? '')}
          onChange={event => onChange('travel_from_location', event.target.value || null)}
        />
      </div>
      <p className="text-muted-foreground text-xs">
        Your estimate gives the planner a starting point. Logged durations and travel times help it
        learn.
      </p>
    </fieldset>
  );
}
